import React from 'react';
import {useForm, Controller} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useLocation, useHistory} from 'react-router-dom';
import {isMobile} from 'react-device-detect';
import moment from 'moment';

import api from 'api';
import {PATHS} from '../../Routes';
import {DATE_FORMAT} from '../../utils/date';
import {useStoredURLParams} from 'context/storedURLParams';
import {useReservation} from 'context/reservation';
import {InputEventType, SelectOptionType} from 'utils/types';
import {useErrorModal, useIsMounted, useStatus} from 'utils/hooks';
import {formatOneDate, getMinCheckInDate} from '../../utils/common';
import {useIpDetails} from '../../context/ipDetails';
import {
  COUNTRY_CODES,
  SINGLE_GROUP_TYPES_OPTIONS,
  GROUP_GROUP_TYPES_OPTIONS,
  ALL_GROUP_TYPES_OPTIONS,
  TOURIST_GROUP_GROUP_TYPE_OPTION,
} from 'utils/constants';
import Input from '../../components/acommon/FieldInput';
import Select from '../../components/acommon/FieldSelect';
import PreviewBackgroundImage from '../../components/PreviewBackgroundImage';
import Page from '../../components/acommon/Page';
import {DimensionsWrapper} from '../../styled/common';
import {ReservationGrid, Form, Logo, Item} from './styled';
import {
  Title,
  GoBackButton,
  ConfirmButton,
  VerticalLine,
  Container,
  DatepickerInput,
} from '../SearchReservationScreen/styled';

enum FORM_NAMES {
  lead_guest_name = 'default_leader_full_name',
  lead_email = 'default_invite_email',
  number_of_guests = 'number_of_guests',
  type_of_registration = 'type',
  check_in_date = 'check_in_date',
  check_out_date = 'check_out_date',
}

type LocationType = {
  l?: string;
  user?: string;
  housingId?: string;
  logo?: string;
  title?: string;
  country?: string;
  startDate?: Date;
  endDate?: Date;
  email?: string;
};

type FormTypes = {
  [FORM_NAMES.lead_guest_name]: string;
  [FORM_NAMES.number_of_guests]: string | number;
  [FORM_NAMES.lead_email]?: string;
  [FORM_NAMES.type_of_registration]?: {label: string; value: string};
  [FORM_NAMES.check_in_date]?: Date;
  [FORM_NAMES.check_out_date]?: Date;
};

function NewReservation() {
  const {t, i18n} = useTranslation();
  const location = useLocation<LocationType>();
  const history = useHistory();
  const {setNewReservationId} = useStoredURLParams();
  const {refreshReservation} = useReservation();
  const {ipDetails} = useIpDetails();
  const {
    setValue,
    register,
    formState: {errors, isSubmitted},
    trigger,
    watch,
    control,
    handleSubmit,
  } = useForm<FormTypes>();
  const isMounted = useIsMounted();
  const {setStatus, isLoading} = useStatus();
  const {ErrorModal, displayError} = useErrorModal();
  const [groupTypes, setGroupTypes] = React.useState<SelectOptionType[]>(
    ALL_GROUP_TYPES_OPTIONS,
  );
  const [foundReservationId, setFoundReservationId] = React.useState('');
  const logo = location?.state?.logo;
  const country = location?.state?.country;
  const housing_id = location?.state?.housingId;
  const l = location?.state?.l;
  const user = location?.state?.user;
  const isTypeShown =
    country === COUNTRY_CODES.austria ||
    country === COUNTRY_CODES.uae ||
    country === COUNTRY_CODES.italy;
  const numberOfGuests = watch(FORM_NAMES.number_of_guests);
  const typeOfRegistration = watch(FORM_NAMES.type_of_registration);

  React.useEffect(
    function preloadKnowsFields() {
      setValue(FORM_NAMES.check_in_date, location?.state?.startDate);
      setValue(FORM_NAMES.check_out_date, location?.state?.endDate);
      setValue(FORM_NAMES.lead_email, location?.state?.email);
    },
    [location, setValue],
  );

  const revalidateFormField = async (name?: FORM_NAMES) => {
    if (isSubmitted && name) {
      await trigger(name);
    }
  };

  const handleInputChange = async (event: InputEventType) => {
    setValue(event.target.name as FORM_NAMES, event.target.value);
    await revalidateFormField();
  };

  React.useEffect(
    function updateGroupTypesOnNumberOfGuestsChange() {
      let defaultAllGroupTypes = [...ALL_GROUP_TYPES_OPTIONS];
      if (country === COUNTRY_CODES.austria) {
        defaultAllGroupTypes.push(TOURIST_GROUP_GROUP_TYPE_OPTION);
      }
      if (numberOfGuests === undefined || numberOfGuests === '') {
        setGroupTypes(defaultAllGroupTypes);
        return;
      }

      let totalGuests = Number(numberOfGuests);
      totalGuests = totalGuests < 0 ? 0 : totalGuests;

      if (totalGuests > 1) {
        if (country === COUNTRY_CODES.austria) {
          setGroupTypes([...GROUP_GROUP_TYPES_OPTIONS, TOURIST_GROUP_GROUP_TYPE_OPTION]);
        } else {
          setGroupTypes(GROUP_GROUP_TYPES_OPTIONS);
        }
        return;
      }

      if (totalGuests > 0) {
        setGroupTypes(SINGLE_GROUP_TYPES_OPTIONS);
      }
    },
    [numberOfGuests, country],
  );

  React.useEffect(
    function keepCorrectRegistrationType() {
      if (!typeOfRegistration || !groupTypes.length) {
        return;
      }

      const hasSelectedGroupTypeOption = groupTypes.find(
        g => g?.value === typeOfRegistration?.value,
      );
      if (!hasSelectedGroupTypeOption) {
        setValue(FORM_NAMES.type_of_registration, undefined);
      }
    },
    [typeOfRegistration, groupTypes, setValue, trigger],
  );

  const onSubmit = async (formData: FormTypes) => {
    setStatus('loading');
    const {number_of_guests, check_out_date, check_in_date, type, ...rest} = formData;
    const payload = {
      ...rest,
      [FORM_NAMES.check_in_date]: moment(check_in_date).format(DATE_FORMAT),
      [FORM_NAMES.check_out_date]: moment(check_out_date).format(DATE_FORMAT),
      guest_group: {
        number_of_guests,
        ...(type && {type: type.value}),
      },
      housing_id,
    };

    const {error, data} = await api.reservations.create(payload);

    if (!isMounted) return;

    if (data) {
      const reservationId = data.id;

      if (reservationId) {
        setNewReservationId(reservationId);
        setFoundReservationId(reservationId);
      }
    }

    if (error) {
      setStatus('error');
      displayError(error);
    }
  };

  React.useEffect(
    function fetchReservation() {
      if (foundReservationId) {
        const refetchReservationAndGo = async () => {
          await refreshReservation();
          setStatus('success');

          history.push(`/?reservation-id=${foundReservationId}&l=${l}&user=${user}`, {
            fromHousingFlow: true,
          });
        };

        refetchReservationAndGo();
      }
    },
    [foundReservationId, history, user, l, refreshReservation, setStatus],
  );

  React.useEffect(
    function changeTranslationErrorMessages() {
      if (isSubmitted) {
        trigger();
      }
    },
    [isSubmitted, i18n.language, trigger],
  );

  const handleGoBack = () => history.push(PATHS.searchReservation, location.state);

  const validateCheckInDate = (value?: string | Date) => {
    if (!value) return value;

    const minCheckInDate = getMinCheckInDate(country);
    const isAfterMinCheckInDate = moment(value).isSameOrAfter(minCheckInDate);
    if (isAfterMinCheckInDate) return true;

    const formatMinCheckInDate = formatOneDate({
      date: minCheckInDate.toDate(),
      ipCountry: ipDetails?.country_code,
      housingCountry: country || '',
    });
    return t(`minimum_check_in_date_is`, {date: formatMinCheckInDate});
  };

  return (
    <Page showFooter footerTitle={location.state?.title}>
      <DimensionsWrapper>
        <PreviewBackgroundImage>
          <Container>
            {logo && <Logo src={logo} alt="random" />}
            {!isMobile && <Title>{t('reservation_details')}</Title>}
            <Form>
              <ReservationGrid>
                <Item>
                  <Controller
                    name={FORM_NAMES.check_in_date}
                    control={control}
                    rules={{required: `${t('required')}`, validate: validateCheckInDate}}
                    render={({field: {onChange, ...restField}, fieldState: {error}}) => (
                      <DatepickerInput
                        disabled={isLoading}
                        error={error?.message}
                        label={t('check_in_date')}
                        onChange={value => onChange(value?.toString())}
                        {...restField}
                      />
                    )}
                  />
                  <Controller
                    name={FORM_NAMES.check_out_date}
                    control={control}
                    rules={{required: `${t('required')}`}}
                    render={({field: {onChange, ...restField}, fieldState: {error}}) => (
                      <DatepickerInput
                        disabled={isLoading}
                        error={error?.message}
                        label={t('check_out_date')}
                        onChange={value => onChange(value?.toString())}
                        {...restField}
                      />
                    )}
                  />
                  <Input
                    type="number"
                    {...register(FORM_NAMES.number_of_guests, {
                      required: `${t('required')}`,
                    })}
                    label={t('guest_to_register')}
                    placeholder={
                      isMobile ? t('enter_number') : t('enter_number_of_guests')
                    }
                    disabled={isLoading}
                    onChange={handleInputChange}
                    error={errors[FORM_NAMES.number_of_guests]?.message}
                  />
                </Item>
                <Item>
                  <VerticalLine />
                </Item>
                <Item>
                  <Input
                    {...register(FORM_NAMES.lead_guest_name, {
                      required: `${t('required')}`,
                    })}
                    disabled={isLoading}
                    label={t('lead_guest_name')}
                    placeholder={t('enter_name')}
                    error={errors[FORM_NAMES.lead_guest_name]?.message}
                  />

                  {isTypeShown && (
                    <Controller
                      name={FORM_NAMES.type_of_registration}
                      control={control}
                      render={({field, fieldState: {error}}) => (
                        <Select
                          options={groupTypes}
                          placeholder={t('select_your_type_of_registration')}
                          label={t('type_of_registration')}
                          disabled={isLoading}
                          error={error?.message}
                          {...field}
                        />
                      )}
                      rules={{required: `${t('required')}`}}
                    />
                  )}
                  <Input
                    {...register(FORM_NAMES.lead_email, {
                      required: `${t('required')}`,
                    })}
                    disabled={isLoading}
                    label={t('lead_guest_email')}
                    placeholder={t('enter_email')}
                    tooltipContentKey={t('lead_guest_email_tooltip')}
                    error={errors[FORM_NAMES.lead_email]?.message}
                  />
                </Item>
              </ReservationGrid>
              <ConfirmButton
                label={isLoading ? `${t('creating')}...` : t('confirm')}
                disabled={isLoading}
                onClick={handleSubmit(onSubmit)}
              />
              <GoBackButton onClick={handleGoBack} label={t('go_back')} secondary />
            </Form>
          </Container>
        </PreviewBackgroundImage>
      </DimensionsWrapper>
      <ErrorModal />
    </Page>
  );
}

export {NewReservation};
